import { Guid, Nillable } from '@web/utils';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { QuantityUnit } from '@web/shared/module/measurement';
import { WarehouseProductType } from '../enums/warehouse-product-type';

export type WarehouseReceiptProductControls = {
  batchNumber: FormControl<Nillable<string>>;
  brand: FormControl<Nillable<string>>;
  durabilityInformation: FormControl<Nillable<string>>;
  id: FormControl<Nillable<Guid>>;
  name: FormControl<Nillable<string>>;
  ownerId: FormControl<Nillable<Guid>>;
  promotion: FormControl<Nillable<boolean>>;
  quantity: FormControl<Nillable<number>>;
  sku: FormControl<Nillable<string>>;
  type: FormControl<Nillable<WarehouseProductType>>;
  unit: FormControl<QuantityUnit>;
};

export type WarehouseReceiptControls = {
  receiptedAt: FormControl<Date>;
  comment: FormControl<Nillable<string>>;
  products: FormArray<WarehouseReceiptProductFormGroup>;
  fileIds: FormControl<Nillable<Guid[]>>;
};

export class WarehouseReceiptProductFormData {
  public static readonly MinQuantity = 0;
  public static readonly MaxSkuLength = 30;
  public static readonly MaxBatchNumberLength = 30;
  public static readonly MaxNameLength = 255;
  public static readonly MaxDurabilityInformationLength = 255;

  public constructor(
    private data: {
      batchNumber?: Nillable<string>;
      brand?: Nillable<string>;
      durabilityInformation?: Nillable<string>;
      id?: Nillable<Guid>;
      name?: Nillable<string>;
      ownerId?: Nillable<Guid>;
      promotion: Nillable<boolean>;
      quantity?: Nillable<number>;
      sku?: Nillable<string>;
      type?: Nillable<WarehouseProductType>;
      unit: QuantityUnit;
    },
  ) {}

  public get id(): Nillable<Guid> {
    return this.data.id;
  }

  public get ownerId(): Nillable<Guid> {
    return this.data.ownerId;
  }

  public get type(): Nillable<WarehouseProductType> {
    return this.data.type;
  }

  public get sku(): Nillable<string> {
    return this.data.sku;
  }

  public get batchNumber(): Nillable<string> {
    return this.data.batchNumber;
  }

  public get name(): Nillable<string> {
    return this.data.name;
  }

  public get unit(): QuantityUnit {
    return this.data.unit;
  }

  public get quantity(): Nillable<number> {
    return this.data.quantity;
  }

  public get durabilityInformation(): Nillable<string> {
    return this.data.durabilityInformation;
  }

  public get brand(): Nillable<string> {
    return this.data.brand;
  }

  public get promotion(): Nillable<boolean> {
    return this.data.promotion;
  }
}

export class WarehouseReceiptFormData {
  public static readonly MaxCommentLength = 50000;
  public static readonly MinProductCount = 0;

  public constructor(
    private data: {
      id?: Nillable<Guid>;
      receiptedAt: Date;
      products: WarehouseReceiptProductFormData[];
      comment: Nillable<string>;
      fileIds: Nillable<Guid[]>;
    },
  ) {}

  public get id(): Nillable<Guid> {
    return this.data.id;
  }

  public get receiptedAt(): Date {
    return this.data.receiptedAt;
  }

  public get products(): WarehouseReceiptProductFormData[] {
    return this.data.products;
  }

  public get comment(): Nillable<string> {
    return this.data.comment;
  }

  public get fileIds(): Nillable<Guid[]> {
    return this.data.fileIds;
  }
}

/* prettier-ignore */
export type WarehouseReceiptProductFormGroup = FormGroup<WarehouseReceiptProductControls>;

/* prettier-ignore */
export type WarehouseReceiptFormGroup = FormGroup<WarehouseReceiptControls>;
